/**
 * V2 Paywall Subscriptions Section Configuration
 *
 * V2 version of subscriptionsSectionConfig.ts with V2 data paths.
 * Product catalog lives in content.paywall.products.
 *
 * V2 path mapping:
 * - V1 subscriptions → V2 content.paywall.products
 * - V1 subscriptionViewKind → V2 viewKind (CatalogViewKind)
 * - V1 itemType → V2 productLayout (ProductLayout)
 * - V1 subscriptionId → V2 productId
 * - V1 leftLabelTop → V2 topStartLabel
 * - V1 leftLabelBottom → V2 bottomStartLabel
 * - V1 rightLabelTop → V2 topEndLabel
 * - V1 rightLabelBottom → V2 bottomEndLabel
 * - V1 isDefault → V2 isSelected
 * - V1 styles → V2 appearance
 * - V1 selectedBlock.styles → V2 selectedAppearance
 * - V1 box.styles → V2 layout.insets
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import { CatalogViewKind } from '@segmently/schema-models/v2';
import {
  createTextStyleFields,
  createListItemStateFields,
  createListCheckboxStyleFields,
  TITLE_TEXT_STYLE_FIELDS,
  V2_TITLE_TEXT_STYLE_FIELDS,
  V2_TEXT_ALIGN_OPTIONS,
} from './atomicFields';

// ============================================
// Cell Type Options (V2 schema enum values)
// ============================================

export const V2_SUBSCRIPTION_CELL_TYPE_OPTIONS = [
  { value: CatalogViewKind.Vertical, label: 'Vertical Cell' },
  { value: CatalogViewKind.Horizontal, label: 'Horizontal Cell' },
] as const;

export type SubscriptionCellType = typeof V2_SUBSCRIPTION_CELL_TYPE_OPTIONS[number]['value'];

export const V2_SUBSCRIPTION_CELL_TYPE_PATH = 'content.paywall.products.viewKind';

// ============================================
// Item Type Options (same UI, V2 path)
// ============================================

export const V2_SUBSCRIPTION_ITEM_TYPE_OPTIONS = [
  { value: 'CheckboxLabels' as const, label: 'Checkbox left' },
  { value: 'LabelsCheckbox' as const, label: 'Checkbox right' },
  { value: 'SubscriptionListItemType1' as const, label: 'No checkbox' },
];

export type SubscriptionItemType = typeof V2_SUBSCRIPTION_ITEM_TYPE_OPTIONS[number]['value'];

export const V2_SUBSCRIPTION_ITEM_TYPE_PATH = 'content.paywall.products.productLayout';

export interface SubscriptionStyleSectionConfig {
  id: string;
  title: string;
  defaultExpanded: boolean;
  fields: FieldConfig[];
  paddingConfig?: {
    basePath: string;
    label?: string;
  };
  visibilityCondition?: (cellType: SubscriptionCellType, itemType: SubscriptionItemType) => boolean;
}

// ============================================
// V2 Style Sections
// ============================================

const V2_PRODUCTS_BASE = 'content.paywall.products';

/**
 * Remap V1 text style fields to V2 TypographyStyle:
 *   .styles.* → .appearance.*
 *   .appearance.textAlign → .appearance.align
 *   textAlign options: left/right → start/end
 */
function remapTextFieldsForV2(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path.replace('.styles.', '.appearance.');
    const isAlignField = path.endsWith('.appearance.textAlign');
    if (isAlignField) {
      path = path.replace('.appearance.textAlign', '.appearance.align');
      return { ...f, path, options: [...V2_TEXT_ALIGN_OPTIONS] };
    }
    return path !== f.path ? { ...f, path } : f;
  });
}

/**
 * Remap V1 flat border fields to V2 nested BorderStyle paths:
 *   .borderWidth → .borders.width
 *   .borderColor → .borders.color
 *   .borderRadius → .borders.radius.uniform
 */
function remapBorderPaths(fields: FieldConfig[]): FieldConfig[] {
  return fields.map(f => {
    let path = f.path;
    if (path.endsWith('.borderWidth')) path = path.replace('.borderWidth', '.borders.width');
    else if (path.endsWith('.borderColor')) path = path.replace('.borderColor', '.borders.color');
    else if (path.endsWith('.borderRadius')) path = path.replace('.borderRadius', '.borders.radius.uniform');
    return path !== f.path ? { ...f, path } : f;
  });
}

/**
 * Create V2 subscription style sections with V2 paths
 * V2 uses appearance/selectedAppearance/layout.insets instead of V1 styles/selectedBlock.styles/box.styles
 */
export function createV2SubscriptionStyleSections(
  basePath: string = V2_PRODUCTS_BASE
): SubscriptionStyleSectionConfig[] {
  return [
    // 1. Unselected item state
    {
      id: 'unselectedState',
      title: 'Unselected item state',
      defaultExpanded: true,
      fields: remapBorderPaths(createListItemStateFields(`${basePath}.appearance`)),
    },
    // 2. Selected item state
    {
      id: 'selectedState',
      title: 'Selected item state',
      defaultExpanded: true,
      fields: remapBorderPaths(createListItemStateFields(`${basePath}.selectedAppearance`)),
    },
    // 3. Item paddings
    {
      id: 'itemPaddings',
      title: 'Item paddings',
      defaultExpanded: false,
      fields: [],
      paddingConfig: {
        basePath: `${basePath}.appearance.insets`,
        label: 'Item padding',
      },
    },
    // 4. Column layout
    {
      id: 'columnLayout',
      title: 'Column layout',
      defaultExpanded: false,
      fields: [
        {
          path: `${basePath}.items.0.appearance.startColumnWidthPercentage`,
          label: 'left column width %',
          type: 'slider',
          defaultValue: 50,
          min: 0,
          max: 100,
          step: 5,
          isNullable: false,
          guideKey: 'screenedit-paywall-subscriptions-column-layout',
        },
      ],
    },
    // 5. List paddings
    {
      id: 'listPaddings',
      title: 'List paddings',
      defaultExpanded: false,
      fields: [],
      paddingConfig: {
        basePath: `${basePath}.layout.insets`,
        label: 'List padding',
      },
    },
    // 5. Top Start Label styles (was Left Top)
    {
      id: 'topStartLabelStyles',
      title: 'Left Top Label styles',
      defaultExpanded: false,
      fields: remapTextFieldsForV2(createTextStyleFields(`${basePath}.items.0.topStartLabel`, V2_TITLE_TEXT_STYLE_FIELDS)),
      paddingConfig: {
        basePath: `${basePath}.items.0.topStartLabel.layout.insets`,
        label: 'Padding',
      },
    },
    // 6. Bottom Start Label styles (was Left Bottom)
    {
      id: 'bottomStartLabelStyles',
      title: 'Left Bottom Label styles',
      defaultExpanded: false,
      fields: remapTextFieldsForV2(createTextStyleFields(`${basePath}.items.0.bottomStartLabel`, V2_TITLE_TEXT_STYLE_FIELDS)),
      paddingConfig: {
        basePath: `${basePath}.items.0.bottomStartLabel.layout.insets`,
        label: 'Padding',
      },
    },
    // 7. Top End Label styles (was Right Top)
    {
      id: 'topEndLabelStyles',
      title: 'Right Top Label styles',
      defaultExpanded: false,
      fields: remapTextFieldsForV2(createTextStyleFields(`${basePath}.items.0.topEndLabel`, V2_TITLE_TEXT_STYLE_FIELDS)),
      paddingConfig: {
        basePath: `${basePath}.items.0.topEndLabel.layout.insets`,
        label: 'Padding',
      },
    },
    // 8. Bottom End Label styles (was Right Bottom)
    {
      id: 'bottomEndLabelStyles',
      title: 'Right Bottom Label styles',
      defaultExpanded: false,
      fields: remapTextFieldsForV2(createTextStyleFields(`${basePath}.items.0.bottomEndLabel`, V2_TITLE_TEXT_STYLE_FIELDS)),
      paddingConfig: {
        basePath: `${basePath}.items.0.bottomEndLabel.layout.insets`,
        label: 'Padding',
      },
    },
    // 9. Badge styles
    {
      id: 'badgeStyles',
      title: 'Badge styles',
      defaultExpanded: false,
      fields: [
        ...createTextStyleFields(`${basePath}.items.0.badge`, TITLE_TEXT_STYLE_FIELDS)
          .map(f => ({
            ...f,
            // V2 BadgeStyle uses 'textColor' instead of 'color'
            path: f.path.replace('.styles.', '.appearance.').replace('.appearance.color', '.appearance.textColor'),
          }))
          .filter(f => !f.path.endsWith('.textAlign') && !f.path.endsWith('.textDecorationColor')),
        // V2 BadgeStyle uses nested borders: { color, width, radius: { uniform } }
        {
          path: `${basePath}.items.0.badge.appearance.borders.radius.uniform`,
          label: 'border radius',
          type: 'number',
          defaultValue: 4,
          min: 0,
          max: 50,
          isNullable: true,
        },
        {
          path: `${basePath}.items.0.badge.appearance.borders.width`,
          label: 'border width',
          type: 'number',
          defaultValue: 0,
          min: 0,
          max: 10,
          isNullable: true,
        },
        {
          path: `${basePath}.items.0.badge.appearance.borders.color`,
          label: 'border color',
          type: 'color',
          defaultValue: '#000000',
          isNullable: true,
        },
        {
          path: `${basePath}.items.0.badge.appearance.position`,
          label: 'position',
          type: 'select',
          defaultValue: 'topRight',
          isNullable: true,
          options: [
            { value: 'topLeft', label: 'Top Left' },
            { value: 'topCenter', label: 'Top Center' },
            { value: 'topRight', label: 'Top Right' },
          ],
        },
      ],
      paddingConfig: {
        basePath: `${basePath}.items.0.badge.appearance.insets`,
        label: 'Badge padding',
      },
    },
    // 10. Checkbox styles (visible only when productLayout includes checkbox)
    // V2 ToggleMark uses appearance/selectedAppearance instead of styles/selectedBlock.styles
    {
      id: 'checkboxStyles',
      title: 'Checkbox styles',
      defaultExpanded: false,
      fields: createListCheckboxStyleFields(`${basePath}.items.0.checkbox`).map(f => ({
        ...f,
        path: f.path
          .replace('.selectedBlock.styles.', '.selectedAppearance.')
          .replace('.styles.', '.appearance.'),
      })),
      paddingConfig: {
        basePath: `${basePath}.items.0.checkbox.layout.insets`,
        label: 'Checkbox padding',
      },
      visibilityCondition: (_cellType: string, itemType?: string) =>
        itemType === 'CheckboxLabels' || itemType === 'LabelsCheckbox',
    },
  ];
}

export const V2_SUBSCRIPTION_STYLE_SECTIONS = createV2SubscriptionStyleSections();

// ============================================
// Array Config (V2 path)
// ============================================

export const V2_SUBSCRIPTIONS_ARRAY_CONFIG: FieldConfig = {
  path: 'content.paywall.products.items',
  label: 'Subscription Plans',
  type: 'text',
  defaultValue: [],
  disableBehavior: 'null',
  guideKey: 'screenedit-paywall-subscriptions-items',
};

// ============================================
// Factory Functions (V2 structures)
// ============================================

/**
 * Create default V2 ProductOffer
 */
export function createDefaultV2ProductOffer(
  locale: string,
  productId: string = '',
): Record<string, unknown> {
  const defaultLabelAppearance = {
    fontSize: 16,
    fontFamily: 'SF Pro',
    fontWeight: 600,
    color: '#ffffff',
    lineHeight: 22,
    align: 'start',
  };

  const createLabel = (text: string) => ({
    translations: { [locale]: text },
    kind: 'static',
    parameters: { labels: {}, links: {} },
    appearance: { ...defaultLabelAppearance },
    layout: { insets: {} },
  });

  return {
    productId,
    isSelected: false,
    weight: 0,
    topStartLabel: createLabel(''),
    bottomStartLabel: createLabel(''),
    topEndLabel: createLabel(''),
    bottomEndLabel: createLabel(''),
    badge: null,
    appearance: {
      startColumnWidthPercentage: 50,
    },
    checkbox: {
      kind: 'square',
      appearance: { color: '#ffffff' },
      selectedAppearance: { color: '#ffc201' },
      layout: { insets: {} },
    },
  };
}

/**
 * Create default V2 ProductCatalog container (structural fallback only).
 * Items are empty — actual products come from theme's Paywall source screen via structuredClone.
 * This fallback is used only when no theme is available.
 */
export function createDefaultV2ProductCatalog(): Record<string, unknown> {
  return {
    viewKind: CatalogViewKind.Vertical,
    productLayout: 'CheckboxLabels',
    items: [],
    appearance: {
      backgroundColor: '#2a2a2a',
      borders: {
        radius: { uniform: 12 },
      },
      insets: {
        start: 16,
        end: 16,
        top: 12,
        bottom: 12,
      },
      itemsSpacing: 8,
    },
    selectedAppearance: {
      backgroundColor: '#ffc201',
      borders: {
        color: '#e7b34c',
        width: 2,
        radius: { uniform: 12 },
      },
    },
    layout: {
      insets: {
        top: 16,
        bottom: 16,
        start: 16,
        end: 16,
      },
    },
  };
}
